import type { Bundle } from 'fhir/r4';
import React, { useEffect, useState } from 'react';
import 'react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css';
import { Message } from 'semantic-ui-react';
import useSWR from 'swr';
import type { SWRResponse } from 'swr';

import { useAuthContext } from '../../../contexts/AuthContext';
import type { Fetched } from '../../../types/food';
import type { ReferralSummary } from '../../../types/patient-data';
import type { PractitionerEntry, ResourceEntry } from '../../../utils/bundleNarrowing';
import type { RejectionWithResponse } from '../types';

import { fetchPatientNotes } from './documentReferenceQueries';
import Tabs from './tabs';
import type { NotesTabsProps } from './tabs';



/**
 * SWR 0.5.7 exports no `KeyedMutator`; the mutator type is declared inline on
 * `SWRResponse`, so it is derived from there rather than hand-rolled.
 */
export type SWRMutate<Data> = SWRResponse<Data, unknown>['mutate'];

/**
 * Transcribed from the producer: `components/patient/index.tsx:529` renders this
 * with `activeTab` state, `fhirPatient.id`, `flatpatient.org`, the
 * `PractitionerEntry[]` in `newPractitionerData`, and the `tasksData`/`connectData`
 * bundles and mutators from its two `useSWR(…, FHIRFetcher<fhir4.Bundle>)` calls.
 */
interface NotesProps {
  /** Same value as `activeTab`; passed twice by the producer, read only as `activeTab`. */
  type: string;
  patientId: string | undefined;
  org: string;
  activeTab: string;
  practitionerData: PractitionerEntry[];
  tasksData: Bundle | undefined;
  tasksMutate: SWRMutate<Bundle | undefined>;
  allReferralData: ReferralSummary[] | undefined;
  connectData: Bundle | undefined;
  connectMutate: SWRMutate<Bundle | undefined>;
}

function Notes(props: NotesProps) {
  const authInfo = useAuthContext();
  const [_loadData, setLoadData] = useState(false);
  // The `entry` array of the `/DocumentReference` bundle, newest first.
  const [notesResponse, setNotesResponse] = useState<ResourceEntry<"DocumentReference">[]>([]);
  const [_tasksNotesResponse, setTasksNotesResponse] = useState([]);
  const [_connectNotesResponse, setConnectNotesResponse] = useState([]);

  const options = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
    onSuccess: (data: Bundle | undefined) => {
      setLoadData(true);

      if (data?.entry?.length) {
        // `fetchPatientNotes` has already filtered this bundle to Care Betty note
        // DocumentReferences, but it does so with boolean predicates rather than
        // type guards, so its declared `Bundle` cannot carry that. Asserted here
        // to the element type the note panes are declared against.
        setNotesResponse([...data.entry].reverse() as ResourceEntry<'DocumentReference'>[]);
        return;
      }

      setNotesResponse([]);
    },
  };

  const {
    data: NotesData,
    error: NotesError,
    mutate,
  } = useSWR<Fetched<fhir4.Bundle>, Error>(
    props.patientId && authInfo.token ? ['patient-notes', props.patientId, authInfo.token] : null,
    // The key above is `null` unless `patientId` is truthy, so SWR never invokes
    // this fetcher with `undefined` — a guarantee the compiler cannot see through
    // the closure, hence the assertion rather than a guard that would change when
    // the request fires.
    () => fetchPatientNotes(authInfo.token, props.patientId as string),
    options
  );

  useEffect(() => {
    if (props.activeTab === 'NOTES') {
       
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; awaiting it would order this render behind a refetch.
      mutate(); //this will trigger SWR to re-Fetch
    }
     
   
  }, [props.activeTab]);

  return (
    <div>
      {NotesData && !NotesError && (
        /* Same two mismatches the `notes/tabs.tsx` prop docs record: SWR's `mutate`
           is parameterised by its own `Data` where `Tabs` names a narrower one, and
           `Bundle.entry` is the whole resource union where the panes name a single
           resource type. Nothing checks `resourceType` on the way through. */
        <Tabs
          style={{ minHeight: '100%' }}
          org={props.org}
          refresh={mutate}
          refreshTasksNotes={props.tasksMutate}
          refreshConnectNotes={props.connectMutate}
          practitionerData={props?.practitionerData}
          patientId={props.patientId}
          notesData={notesResponse}
          tasksNotesData={props.tasksData?.entry as NotesTabsProps['tasksNotesData']}
          connectNotesData={props.connectData?.entry as NotesTabsProps['connectNotesData']}
          setNotesResponse={setNotesResponse}
          setTasksNotesResponse={setTasksNotesResponse}
          setConnectNotesResponse={setConnectNotesResponse}
          allReferralData={props?.allReferralData}
        />
      )}
      {NotesError && (
        <Message negative>
          <Message.Header>Unable to load notes</Message.Header>
          <p>
            {(NotesError as RejectionWithResponse | undefined)?.response?.data?.errorMessage ||
              NotesError?.message ||
              'The notes request failed. This can happen when a patient has large uploaded documents.'}
          </p>
        </Message>
      )}
    </div>
  );
}

export default Notes;
